import React, { useEffect, useState } from 'react';

import { styled } from '@this/constants/themes';
import type TransportElement from '@this/domain/transport_element2';
import { ButtonBase } from '@this/shared/atoms/button';
import type TravelerInformation from '@this/domain/traveler_information';
import type User from '@this/domain/user/user2';
import type { Moment } from '@this/src/lib/moment';
import { observer } from 'mobx-react';
import { Link } from '@this/shared/ui/navigations/link';
import OrderItemTravelerInputsForm from './order_item_traveler_inputs_form';
import OrderItemTravelerSelect from './order_item_traveler_select';
import OrderItemTravelerDomesticAirForm from './order_item_traveler_domestic_air_form';
import OrderItemTravelerForeignAirForm from './order_item_traveler_foreign_air_form';
import OrderItemTravelerRailwayForm from './order_item_traveler_railway_form';

type Props = {
  transportType: TransportElement['transportType'] | null;
  transportDate: Moment | null;
  travelerInformation: TravelerInformation;
  className?: string;
  candidates: User[];
  candidatesLoading: boolean;
  errors?: string[];
  onSearchCandidates: (query: string) => void;
  onSubmit: (travelerInformation: TravelerInformation) => void;
  onCancel: () => void;
  classNameForModifiedField: (...path: (string | number)[]) => string;
};

const USER_DEFAULT_FORMDATA = {
  userId: undefined,
  email: null,
  firstNameKana: '',
  lastNameKana: '',
  firstNameRoman: '',
  lastNameRoman: '',
  middleNameRoman: '',
  flightFirstName: '',
  flightLastName: '',
  flightMiddleName: '',
  flightFirstNameKana: '',
  flightLastNameKana: '',
  flightFirstNameRoman: '',
  flightLastNameRoman: '',
  flightMiddleNameRoman: '',
  birthday: '',
  flightBirthday: '',
  flightGender: null
};

const TRANSPORT_DEFAULT_FORMDATA = {
  airSeat: undefined,
  shinkansenSeat: '指定なし' as const,
  flightTel: null,
  passportNumber: null,
  passportExpire: null,
  passportIssuedCountry: '日本',
  mileageNumber: null
};

const hasManualValues = (travelerInformation?: TravelerInformation): boolean => {
  if (!travelerInformation) return false;
  const candidateValues = [
    travelerInformation.lastNameKana,
    travelerInformation.firstNameKana,
    travelerInformation.lastNameRoman,
    travelerInformation.firstNameRoman,
    travelerInformation.middleNameRoman,
    travelerInformation.birthday,
    travelerInformation.flightGender,
    travelerInformation.flightFirstName,
    travelerInformation.flightLastName,
    travelerInformation.flightBirthday
  ];
  return candidateValues.some(value => {
    if (value == null) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return true;
  });
};

const manualNameAttrs: (keyof TravelerInformation)[] = [
  'firstNameRoman',
  'lastNameRoman',
  'firstNameKana',
  'lastNameKana'
];

const manualRequiredAttrs: (keyof TravelerInformation)[] = [...manualNameAttrs, 'birthday', 'flightGender'];

const OrderItemTravelerForm: React.FC<Props> = ({
  candidates,
  className,
  travelerInformation,
  transportDate,
  transportType,
  candidatesLoading,
  errors,
  onSubmit,
  onCancel,
  onSearchCandidates,
  classNameForModifiedField
}) => {
  const [isManualInput, setIsManualInput] = useState<boolean>(() => {
    if (!travelerInformation) return false;
    if (travelerInformation.userId) return false;
    return hasManualValues(travelerInformation);
  });
  type ValidationState = 'idle' | 'dirty';
  const [inputErrorMessages, setInputErrorMessages] = useState<string[]>([]);
  const [manualValidationState, setManualValidationState] = useState<ValidationState>('idle');

  useEffect(() => {
    if (!isManualInput) {
      // 初期表示のため
      onSearchCandidates('');
    }
  }, [isManualInput, onSearchCandidates, travelerInformation]);

  const canInputTransportInfo = (): boolean => {
    if (!travelerInformation) {
      return false;
    }

    // 手動入力の場合、航空券フォームを表示する前に必須項目が揃っているかを確認する
    if (isManualInput) {
      return manualRequiredAttrs.every(key => !!travelerInformation[key]);
    }

    // 検索入力の場合は候補を選択してから詳細入力を表示する
    return !!travelerInformation.userId;
  };

  const shouldShowFlightForm = canInputTransportInfo();

  useEffect(() => {
    if (transportType !== 'domestic_air' && transportType !== 'foreign_air') {
      return;
    }
    if (!travelerInformation) {
      return;
    }
    if (isManualInput) {
      if (manualValidationState !== 'dirty') {
        setInputErrorMessages([]);
        return;
      }
    } else if (!travelerInformation.userId) {
      setInputErrorMessages([]);
      return;
    }

    if (!isManualInput) {
      setInputErrorMessages([]);
      return;
    }
    if (manualValidationState !== 'dirty') {
      setInputErrorMessages([]);
      return;
    }

    const birthDayValid = validateBirthDay();
    const manualNamesValid = manualNameAttrs.every(key => !!travelerInformation[key]);

    const errorMessages = [];
    if (!birthDayValid) {
      errorMessages.push('誕生日をYYYY-MM-DD形式で入力してください');
    }
    if (!manualNamesValid) {
      errorMessages.push('氏名(ローマ字)、氏名(カナ)を入力してください');
    }

    setInputErrorMessages(errorMessages);
  }, [
    isManualInput,
    manualValidationState,
    travelerInformation?.birthday,
    travelerInformation?.lastNameRoman,
    travelerInformation?.firstNameRoman,
    transportType
  ]);

  useEffect(() => {
    if (transportType === 'domestic_air' || transportType === 'foreign_air') {
      // 検索入力モードの場合のみ航空券情報の必須チェックを行う。
      if (!isManualInput) {
        if (!travelerInformation?.userId) {
          return;
        }

        const flightBirthDayValid = validateFlightBirthDay();
        const flightNameValid = validateFlightName();

        const errorMessages = [];
        if (!flightBirthDayValid) {
          errorMessages.push('誕生日をYYYY-MM-DD形式で入力してください');
        }
        if (!flightNameValid) {
          errorMessages.push('氏名(ローマ字)を入力してください');
        }

        setInputErrorMessages(errorMessages);
      }
    }
  }, [
    isManualInput,
    travelerInformation?.flightBirthday,
    travelerInformation?.flightFirstName,
    travelerInformation?.flightLastName,
    transportType
  ]);

  const birthdayPattern = /^\d{4}-\d{2}-\d{2}$/;
  const validateBirthDay = (): boolean => {
    return (
      !!travelerInformation.birthday && birthdayPattern.test(travelerInformation.birthday.format('YYYY-MM-DD'))
    );
  };
  const validateFlightBirthDay = (): boolean => {
    if (!travelerInformation.flightBirthday) {
      return false;
    }
    return (
      !!travelerInformation.flightBirthday &&
      birthdayPattern.test(travelerInformation.flightBirthday.format('YYYY-MM-DD'))
    );
  };

  const validateFlightName = (): boolean => {
    if (!travelerInformation.flightLastName || !travelerInformation.flightFirstName) {
      return false;
    }
    return travelerInformation.flightLastName.trim() !== '' && travelerInformation.flightFirstName.trim() !== '';
  };
  const canSubmit = (): boolean => {
    return inputErrorMessages.length === 0;
  };

  return (
    <div className={className}>
      <TravelerBaseInfoContainer>
        <div>
          {isManualInput ? (
            <OrderItemTravelerInputsForm
              travelerInformation={travelerInformation}
              transportDate={transportDate}
              classNameForModifiedField={classNameForModifiedField}
              transportType={transportType}
              onAnyChange={() => setManualValidationState('dirty')}
            />
          ) : (
            <OrderItemTravelerSelect
              selectedUserName={`${travelerInformation.lastNameKana || ''} ${
                travelerInformation.firstNameKana || ''
              }`}
              candidates={candidates}
              loading={candidatesLoading}
              onSearch={onSearchCandidates}
              onChange={c => travelerInformation.assignFromUser(c)}
              className={`
                ${classNameForModifiedField('lastNameKana')}
                ${classNameForModifiedField('firstNameKana')}
              `}
            />
          )}
        </div>
        <Link
          type="button"
          onClick={() => {
            const next = !isManualInput;
            setIsManualInput(next);
            if (next) {
              setInputErrorMessages([]);
              setManualValidationState('idle');
            }
          }}
        >
          {isManualInput ? '検索入力' : '手動入力'}
        </Link>
      </TravelerBaseInfoContainer>

      {shouldShowFlightForm ? (
        transportType === 'domestic_air' ? (
          <OrderItemTravelerDomesticAirForm
            travelerInformation={travelerInformation}
            transportDate={transportDate}
            hiddenPersonalForm={isManualInput}
            classNameForModifiedField={classNameForModifiedField}
          />
        ) : transportType === 'foreign_air' ? (
          <OrderItemTravelerForeignAirForm
            travelerInformation={travelerInformation}
            transportDate={transportDate}
            hiddenPersonalForm={isManualInput}
            classNameForModifiedField={classNameForModifiedField}
          />
        ) : transportType === 'shinkansen' || transportType === 'express' ? (
          <OrderItemTravelerRailwayForm
            travelerInformation={travelerInformation}
            classNameForModifiedField={classNameForModifiedField}
          />
        ) : (
          ''
        )
      ) : (
        ''
      )}
      {errors && errors.length > 0 && (
        <OrderItemError>
          {errors.map((error, i) => (
            <Error key={i}>{error}</Error>
          ))}
        </OrderItemError>
      )}
      {travelerInformation &&
        inputErrorMessages &&
        inputErrorMessages.length > 0 &&
        inputErrorMessages.map((error, i) => <Error key={i}>{error}</Error>)}
      <Actions>
        <Link type="button" onClick={onCancel}>
          キャンセル
        </Link>
        <SubmitButton
          onClick={() =>
            onSubmit(travelerInformation ?? { ...USER_DEFAULT_FORMDATA, ...TRANSPORT_DEFAULT_FORMDATA })
          }
          disabled={!canSubmit()}
        >
          更新
        </SubmitButton>
      </Actions>
    </div>
  );
};

const TravelerBaseInfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 5px;
`;

const OrderItemError = styled.div`
  padding: 10px 0;
`;

const Error = styled.div`
  color: ${props => props.theme.redColor};
`;

const SubmitButton = styled.button.attrs({ type: 'button' })`
  ${ButtonBase};
  padding: 5px 10px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 15px;

  > * + * {
    margin-left: 5px;
  }
`;

export default observer(OrderItemTravelerForm);
